import type { AnhangEntwurf } from "./ticketService";
import type { TicketArt, TicketBereich } from "./ticketTypen";

/**
 * Entwurf des Melde-Dialogs (User 29.09.2026: „wenn man ein Ticket schreibt und
 * es zumacht, dann muss der Text und die Bilder erhalten bleiben"). Schließen
 * und Abbrechen verwerfen nichts mehr; erst „Zurücksetzen" oder erfolgreiches
 * Senden leeren den Entwurf.
 *
 * Anhänge (Blobs) liegen nur im Speicher, sie überstehen also Schließen und
 * Seitenwechsel, aber kein Neuladen. Die Texte stehen zusätzlich im
 * localStorage und überstehen auch das Neuladen.
 */
export type TicketEntwurf = {
  art: TicketArt;
  betreff: string;
  inhalt: string;
  /** Nur gesetzt, wenn der Bereich von Hand gewählt wurde — sonst gilt die Seite. */
  bereich: TicketBereich | null;
  anhaenge: AnhangEntwurf[];
};

const SCHLUESSEL = "kmvs.ticketEntwurf";

const leer = (): TicketEntwurf => ({ art: "problem", betreff: "", inhalt: "", bereich: null, anhaenge: [] });

let speicher: TicketEntwurf | null = null;

function ausLocalStorage(): TicketEntwurf {
  try {
    const roh = window.localStorage.getItem(SCHLUESSEL);
    if (!roh) return leer();
    const d = JSON.parse(roh) as Partial<TicketEntwurf>;
    return {
      art: d.art === "idee" ? "idee" : "problem",
      betreff: typeof d.betreff === "string" ? d.betreff : "",
      inhalt: typeof d.inhalt === "string" ? d.inhalt : "",
      bereich: typeof d.bereich === "string" ? (d.bereich as TicketBereich) : null,
      anhaenge: [],
    };
  } catch {
    return leer();
  }
}

export function ladeEntwurf(): TicketEntwurf {
  if (!speicher) speicher = ausLocalStorage();
  return { ...speicher, anhaenge: [...speicher.anhaenge] };
}

export function merkeEntwurf(e: TicketEntwurf): void {
  speicher = { ...e, anhaenge: [...e.anhaenge] };
  try {
    if (istLeer(e)) window.localStorage.removeItem(SCHLUESSEL);
    else window.localStorage.setItem(SCHLUESSEL, JSON.stringify({ art: e.art, betreff: e.betreff, inhalt: e.inhalt, bereich: e.bereich }));
  } catch {
    /* ohne localStorage bleibt der Entwurf nur im Speicher */
  }
}

export function verwirfEntwurf(): void {
  speicher = leer();
  try {
    window.localStorage.removeItem(SCHLUESSEL);
  } catch {
    /* s. o. */
  }
}

export function istLeer(e: TicketEntwurf): boolean {
  return !e.betreff.trim() && !e.inhalt.trim() && e.anhaenge.length === 0 && e.bereich === null && e.art === "problem";
}

/** Nur für Tests: Speicher vergessen, als wäre die Seite neu geladen. */
export function _entwurfSpeicherVergessen(): void {
  speicher = null;
}
